import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import API from '../services/api';
import { citySlug } from '../services/citySlug';
import { PLATFORM_FEE } from '../services/fees';
import { filterTestDoctors } from '../services/testHospitals';
import SEO from './SEO';
import CityIntent from './CityIntent';
import { providerLabel } from '../services/providerLabel';

const STEP_ICONS = ['bi-search', 'bi-calendar-check', 'bi-credit-card', 'bi-hospital'];

// Inline vector logos for each specialty chip. Crisp on every device (no emoji
// tofu), inherit the chip's text color via currentColor, and scale cleanly.
// Keys match SPECIALTIES[].key below.
const SPEC_ICONS = {
  general: (
    <>
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </>
  ),
  heart: (
    <>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
      <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" />
    </>
  ),
  skin: (
    <>
      <rect x="2.5" y="8" width="19" height="8" rx="4" transform="rotate(45 12 12)" />
      <path d="M9.5 9.5v5M14.5 9.5v5" />
    </>
  ),
  dental: (
    <path d="M7.5 3.5C5.5 3.5 3.5 5 3.5 8c0 1.8.5 3.3 1 5.5.4 1.8.6 4 1.5 5.5.6 1 1.6.8 2-.5l1-3.5c.2-.7.4-1 1-1s.8.3 1 1l1 3.5c.4 1.3 1.4 1.5 2 .5.9-1.5 1.1-3.7 1.5-5.5.5-2.2 1-3.7 1-5.5 0-3-2-4.5-4-4.5-1.6 0-2.8.8-4 1.8-1.2-1-2.4-1.8-4-1.8Z" />
  ),
  child: (
    <>
      <path d="M9 12h.01M15 12h.01" />
      <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
      <path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5S14.5 8 13 8s-2.5-1.1-2.5-2.5" />
    </>
  ),
  bones: (
    <path d="M17 10c.7-.7 1.69 0 2.5 0a2.5 2.5 0 1 0 0-5 .5.5 0 0 1-.5-.5 2.5 2.5 0 1 0-5 0c0 .81.7 1.8 0 2.5l-7 7c-.7.7-1.69 0-2.5 0a2.5 2.5 0 0 0 0 5c.28 0 .5.22.5.5a2.5 2.5 0 1 0 5 0c0-.81-.7-1.8 0-2.5Z" />
  ),
  eye: (
    <>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  ent: (
    <>
      <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0" />
      <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 1 1 0 4" />
    </>
  ),
  women: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="M12 15v7M9 19h6" />
    </>
  ),
  neuro: (
    <>
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
    </>
  ),
  mental: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <path d="M9 9h.01M15 9h.01" />
    </>
  ),
  diabetes: (
    <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7Z" />
  ),
  kidney: (
    <path d="M14.5 4C10 4 6 7 6 12s4 8 8.5 8c2 0 3.5-1.2 3.5-3 0-1.5-1-2.2-1-3.5 0-1 .8-1.5 1.5-2 .7-.5 1.5-1.2 1.5-2.5C20 6 17.5 4 14.5 4Z" />
  ),
  stomach: (
    <path d="M8 3v5a5 5 0 0 0 5 5 3 3 0 0 1 3 3 4 4 0 0 1-8 0" />
  ),
  lungs: (
    <>
      <path d="M12 4v7" />
      <path d="M9 11a3 3 0 0 0-3 3v3a2 2 0 0 0 4 0v-4a2 2 0 0 0-1-2Z" />
      <path d="M15 11a3 3 0 0 1 3 3v3a2 2 0 0 1-4 0v-4a2 2 0 0 1 1-2Z" />
    </>
  ),
  physio: (
    <>
      <path d="m6.5 6.5 11 11" />
      <path d="m21 21-1-1M3 3l1 1" />
      <path d="m18 22 4-4M2 6l4-4" />
      <path d="m3 10 7-7M14 21l7-7" />
    </>
  ),
};

// Renders a specialty logo as inline SVG. Inherits color/size from CSS.
function SpecIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {SPEC_ICONS[name]}
    </svg>
  );
}

// Quick-access specialty tiles. `key` is the search term passed to /alldoctor
// (`?q=key`); AllDoctor expands it via SPEC_SYNONYMS so e.g. "skin" also finds
// doctors stored as "Dermatologist". Labels are translated (hero.specialties.*).
const SPECIALTIES = [
  { key: 'general'  },
  { key: 'heart'    },
  { key: 'skin'     },
  { key: 'dental'   },
  { key: 'child'    },
  { key: 'bones'    },
  { key: 'eye'      },
  { key: 'ent'      },
  { key: 'women'    },
  { key: 'neuro'    },
  { key: 'mental'   },
  { key: 'diabetes' },
  { key: 'kidney'   },
  { key: 'stomach'  },
  { key: 'lungs'    },
  { key: 'physio'   },
];

// The cities, the hospitals and their doctor counts all come from the one public
// doctor list, so every number on this page is a real one and a new city or
// hospital appears with no code change (CityDoctors groups the same way).
function summarise(doctors) {
  const cities = new Map();
  const hospitals = new Map();
  doctors.forEach((d) => {
    const slug = citySlug(d.city);
    if (slug) {
      const c = cities.get(slug) || { slug, name: d.city.trim(), doctors: 0, hospitalIds: new Set() };
      c.doctors += 1;
      c.hospitalIds.add(d.hospital);
      cities.set(slug, c);
    }
    const h = hospitals.get(d.hospital)
      || { id: d.hospital, name: d.hospital_name, city: (d.city || '').trim(), doctors: 0 };
    h.doctors += 1;
    hospitals.set(d.hospital, h);
  });
  return {
    cities: [...cities.values()]
      .map(({ hospitalIds, ...c }) => ({ ...c, hospitals: hospitalIds.size }))
      .sort((a, b) => b.doctors - a.doctors),
    hospitals: [...hospitals.values()]
      .sort((a, b) => b.doctors - a.doctors || String(a.name).localeCompare(String(b.name))),
  };
}

// Most hospitals have not uploaded a photo; their card gets the doctor's
// initials rather than a tall empty box.
const hasPhoto = (d) => !!d.image && !d.image.includes('placehold');
const initials = (name) => String(name || '').replace(/^dr\.?\s+/i, '').split(/\s+/)
  .filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

const MAX_CITIES = 5;
const MAX_HOSPITALS = 8;
const MAX_DOCTORS = 8;

export default function Hero() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [doctors,    setDoctors]    = useState(null); // null until the list arrives
  const [heroSearch, setHeroSearch] = useState('');
  const [pickedCity, setPickedCity] = useState(null); // the city whose question is open
  // Advertised pre-login — PassView is open to anonymous callers precisely so
  // this can ask before a visitor has an account. `enabled` is the real
  // PASS_ENABLED flag either way, so the kill switch hides this same as it
  // hides the offer at checkout. A failed/slow fetch just means no teaser,
  // never a broken landing page.
  const [passOffer, setPassOffer] = useState(null);

  const handleHeroSearch = (e) => {
    e.preventDefault();
    const q = heroSearch.trim();
    navigate(q ? `/alldoctor?q=${encodeURIComponent(q)}` : '/alldoctor');
  };

  const STEPS = t('hero.process.steps', { returnObjects: true }).map((title, i) => ({ title, icon: STEP_ICONS[i] }));
  const TRUST = t('hero.trust', { returnObjects: true });

  useEffect(() => {
    let live = true;
    API.get('/doctors/')
      .then(({ data }) => {
        if (live) setDoctors(filterTestDoctors(Array.isArray(data) ? data : (data.results || [])));
      })
      .catch(() => { if (live) setDoctors([]); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    API.get('/payment/pass/').then(({ data }) => setPassOffer(data)).catch(() => {});
  }, []);

  const loading = doctors === null;
  const { cities, hospitals } = useMemo(() => summarise(doctors || []), [doctors]);
  // Doctors who can be booked today first; the server's order (popular first)
  // breaks ties because Array.sort is stable.
  const featured = useMemo(
    () => [...(doctors || [])].sort((a, b) => Number(!!b.available) - Number(!!a.available)).slice(0, MAX_DOCTORS),
    [doctors],
  );
  const showCities = loading || cities.length > 0;

  return (
    <>
    <SEO
      title="Book Doctor Appointments Online — Skip Hospital Queue"
    description="TokenWalla lets you book doctor appointments online in Andhra Pradesh & Telangana. Get a digital OPD token, track your live queue position, and walk in right on time. No more waiting rooms."
    keywords="book doctor appointment online, hospital token booking AP, skip hospital queue, OPD booking Telangana, doctor appointment Hindupur"
    url="/"
    />
      <style>{css}</style>
      {pickedCity && <CityIntent city={pickedCity} onClose={() => setPickedCity(null)} />}

      <div className="hero-root">

        {/* ── HERO ── */}
        <section className="hm-hero">
          <div className="hm-glow" />
          <div className="tw-container hm-hero-inner fade-up">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              {t('hero.badge')}
            </div>
            <h1 className="hero-title">
              {t('hero.titleLine1')}<br />
              <span className="accent">{t('hero.titleLine2')}</span> {t('hero.titleLine3')}
            </h1>
            <p className="hero-sub">{t('hero.subtitle')}</p>

            <form className="hero-search" onSubmit={handleHeroSearch} role="search">
              <span className="hero-search-icon" aria-hidden="true"><i className="bi bi-search me-1" /></span>
              <input
                className="hero-search-input"
                type="text"
                value={heroSearch}
                onChange={e => setHeroSearch(e.target.value)}
                placeholder={t('hero.searchPlaceholder')}
                aria-label={t('hero.searchPlaceholder')}
              />
              <button type="submit" className="hero-search-btn">{t('hero.searchBtn')}</button>
            </form>

            {/* The city buttons. Not an <aside>: App.css has a stray global
                `aside { text-align: center }` (and a red `aside button`). */}
            {showCities && (
              <div className="hm-cities" role="group" aria-label={t('hero.cityLabel')}>
                <div className="hm-cities-label"><i className="bi bi-geo-alt-fill" /> {t('hero.cityLabel')}</div>
                <div className="hm-cities-row">
                  {loading && <div className="hm-city hm-skel" aria-hidden="true" />}
                  {cities.slice(0, MAX_CITIES).map(c => (
                    <Link
                      key={c.slug}
                      to={`/doctors/${c.slug}`}
                      className="hm-city"
                      aria-haspopup="dialog"
                      onClick={(e) => {
                        // A plain tap asks what they are looking for; a modified
                        // click or middle-click still opens the city page.
                        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        e.preventDefault();
                        setPickedCity(c);
                      }}
                    >
                      <span className="hm-city-pin"><i className="bi bi-geo-alt-fill" /></span>
                      <span className="hm-city-body">
                        <span className="hm-city-name">{c.name}</span>
                        <span className="hm-city-meta">
                          {t('hero.doctorsCount', { count: c.doctors })} · {t('hero.hospitalsCount', { count: c.hospitals })}
                        </span>
                      </span>
                      <i className="bi bi-arrow-right hm-city-go" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="hm-actions">
              <Link to="/alldoctor" className="btn-primary hero-book-btn">{t('hero.bookAppointment')}</Link>
              {hospitals.length > 0 && (
                // A button, not a #hash link: ScrollManager (Routing.js) scrolls
                // to the top on every pushed navigation, hash included.
                <button
                  type="button"
                  className="btn-outline hero-book-btn"
                  onClick={() => document.getElementById('hospitals')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })}
                >
                  <i className="bi bi-hospital" /> {t('hero.stats.hospitals')}
                </button>
              )}
            </div>

            <ul className="hm-trust">
              {TRUST.map(item => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </section>

        {/* ── SPECIALTIES ── */}
        <section className="hm-section">
          <div className="tw-container">
            <div className="tw-section-label">{t('hero.specialtiesLabel')}</div>
            <div className="hm-spec-grid">
              {SPECIALTIES.map(s => (
                <Link key={s.key} to={`/alldoctor?q=${s.key}`} className="hm-spec">
                  <span className="hm-spec-icon"><SpecIcon name={s.key} /></span>
                  <span className="hm-spec-name">{t(`hero.specialties.${s.key}`)}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOSPITALS ── */}
        {hospitals.length > 0 && (
          <section className="hm-section hm-alt" id="hospitals">
            <div className="tw-container">
              <div className="hm-head">
                <div>
                  <div className="tw-section-label">{t('hero.stats.hospitals')}</div>
                  <h2>{t('hero.hospitalsHeading')}</h2>
                </div>
                {/* The city page lists every hospital with its doctors. */}
                <Link to={`/doctors/${cities[0].slug}`} className="btn-outline hm-viewall">
                  {t('hero.doctorsPreview.viewAll')}
                </Link>
              </div>
              <div className="hm-hosp-grid">
                {hospitals.slice(0, MAX_HOSPITALS).map(h => (
                  <Link key={h.id} to={`/alldoctor?q=${encodeURIComponent(h.name)}`} className="hm-hosp">
                    <span className="hm-hosp-icon"><i className="bi bi-hospital" /></span>
                    <span className="hm-hosp-body">
                      <span className="hm-hosp-name">{h.name}</span>
                      <span className="hm-hosp-meta">
                        {h.city && <><i className="bi bi-geo-alt" /> {h.city} · </>}
                        {t('hero.doctorsCount', { count: h.doctors })}
                      </span>
                    </span>
                    <i className="bi bi-chevron-right hm-hosp-go" />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── DOCTORS ── */}
        {featured.length > 0 && (
          <section className="hm-section">
            <div className="tw-container">
              <div className="hm-head">
                <div>
                  <div className="tw-section-label">{t('hero.doctorsPreview.label')}</div>
                  <h2>{t('hero.doctorsPreview.heading')}</h2>
                </div>
                <Link to="/alldoctor" className="btn-outline hm-viewall">{t('hero.doctorsPreview.viewAll')}</Link>
              </div>
              <div className="hm-doc-grid">
                {featured.map(doc => (
                  <Link to={`/doctor/${doc.id}`} className="hm-doc" key={doc.id}>
                    <div className="hm-doc-photo">
                      {hasPhoto(doc)
                        ? <img src={doc.image} alt="" loading="lazy" />
                        : <span className="hm-doc-initials">{initials(doc.name)}</span>}
                      {doc.available && <span className="hm-doc-live">{t('doctors.available')}</span>}
                    </div>
                    <div className="hm-doc-info">
                      <div className="hm-doc-spec">{doc.specialization}</div>
                      <div className="hm-doc-name">{providerLabel(doc.name)}</div>
                      <div className="hm-doc-hosp"><i className="bi bi-hospital" /> {doc.hospital_name}</div>
                      <div className="hm-doc-meta">
                        <span><i className="bi bi-geo-alt" /> {doc.city}</span>
                        <span>{t('hero.doctorsPreview.yearsExp', { count: doc.experience })}</span>
                      </div>
                    </div>
                    <span className="hm-doc-book">{t('doctors.bookNow')}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── HOW IT WORKS ── */}
        <section className="hm-section hm-alt">
          <div className="tw-container">
            <ol className="hm-steps">
              {STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="hm-step-icon"><i className={`bi ${s.icon}`} /></span>
                  <span><span className="hm-step-num">0{i + 1}</span>{s.title}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── FEES ── */}
        <section className="hm-section">
          <div className="tw-container">
            <div className="hm-fee-row">
              <div className="hm-fee">
                <div className="hm-fee-name">{t('hero.pricing.planName')}</div>
                <div className="hm-fee-amt"><sup>₹</sup>{PLATFORM_FEE}</div>
                <p className="hm-fee-note">{t('hero.pricing.planSub')}<br />{t('hero.pricing.note')}</p>
              </div>
              {passOffer?.enabled && (
                <div className="hm-fee hm-fee-pass">
                  <span className="hm-fee-badge">{t('hero.pricing.passBadge')}</span>
                  <div className="hm-fee-name">{t('hero.pricing.passName')}</div>
                  <div className="hm-fee-amt"><sup>₹</sup>{Math.round(Number(passOffer.price))}</div>
                  <p className="hm-fee-note">
                    {t('hero.pricing.passSub', { bookings: passOffer.bookings, days: passOffer.days })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── FOR HOSPITALS ── */}
        <section className="hm-section" style={{ paddingTop: 0 }}>
          <div className="tw-container">
            <div className="hm-biz">
              <h2>{t('hero.forHospitals')}</h2>
              <div className="hm-biz-actions">
                <Link to="/Hlogin" className="btn-white">{t('hero.hospitalLogin')}</Link>
                <Link to="/Husercreate" className="btn-white-outline">{t('hero.cta.registerHospital')}</Link>
                {/* Centres reach the SAME form — the query param only preselects
                    the toggle. Without a link that says the words "scanning
                    centre", a centre owner has no way to know the page is for
                    them. */}
                <Link to="/Husercreate?kind=SCAN_CENTER" className="btn-white-outline">{t('hero.cta.registerScanCenter')}</Link>
                <Link to="/Husercreate?kind=BLOOD_CENTER" className="btn-white-outline">{t('hero.cta.registerBloodCenter')}</Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}

const css = `
  .hero-root { font-family: var(--font-body); background: #fff; color: var(--gray-900); }

  /* ── HERO ── */
  .hm-hero {
    position: relative; overflow: hidden;
    padding: clamp(36px, 6vw, 72px) 0 clamp(36px, 5vw, 56px);
    background: linear-gradient(180deg, var(--blue-50) 0%, #FFFFFF 100%);
  }
  .hm-glow {
    position: absolute; top: -160px; right: -140px;
    width: 560px; height: 560px; border-radius: 50%;
    background: radial-gradient(circle, rgba(55,138,221,0.12) 0%, transparent 70%);
    pointer-events: none;
  }
  .hm-hero-inner { position: relative; max-width: 880px; text-align: center; }

  .hero-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: #fff; border: 1px solid var(--blue-100);
    border-radius: 100px; padding: 6px 16px;
    font-size: 13px; font-weight: 500; color: var(--blue-700);
    box-shadow: var(--shadow-sm); margin-bottom: 22px;
  }
  .hero-badge-dot {
    width: 7px; height: 7px; border-radius: 50%; background: var(--color-success-text);
    animation: twPulse 2s ease-in-out infinite;
  }
  .hero-title {
    font-family: var(--font-display);
    font-size: clamp(2.2rem, 5vw, 3.8rem);
    font-weight: 800; line-height: 1.12; margin-bottom: 16px; color: var(--gray-900);
  }
  .hero-title .accent { color: var(--blue-600); }
  .hero-sub {
    font-size: 1.05rem; color: var(--gray-600); line-height: 1.7;
    max-width: 540px; margin: 0 auto 28px;
  }

  .hero-search {
    display: flex; align-items: center; gap: 8px; text-align: left;
    background: #fff; border: 1px solid var(--blue-100);
    border-radius: 16px; padding: 8px 8px 8px 18px;
    box-shadow: var(--shadow-md); max-width: 620px; margin: 0 auto 26px;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .hero-search:focus-within { border-color: var(--blue-400); box-shadow: var(--shadow-lg); }
  .hero-search-icon { font-size: 17px; color: var(--gray-400); line-height: 1; }
  .hero-search-input {
    flex: 1; border: none; outline: none; background: transparent;
    font-family: var(--font-body); font-size: 15px; color: var(--gray-900);
    padding: 12px 4px; min-width: 0;
  }
  .hero-search-input::placeholder { color: var(--gray-400); }
  .hero-search-btn {
    flex-shrink: 0; border: none; cursor: pointer;
    background: var(--blue-600); color: #fff;
    font-family: var(--font-body); font-size: 14.5px; font-weight: 600;
    border-radius: 11px; padding: 12px 22px; transition: background 0.15s;
  }
  .hero-search-btn:hover { background: var(--blue-700); }

  /* City buttons: the page's way in for "doctors near me" */
  .hm-cities { margin-bottom: 26px; }
  .hm-cities-label {
    display: inline-flex; align-items: center; gap: 7px; margin-bottom: 12px;
    font-size: 12px; font-weight: 700; letter-spacing: 0.8px;
    text-transform: uppercase; color: var(--gray-500);
  }
  .hm-cities-label i { color: var(--blue-600); font-size: 14px; }
  .hm-cities-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; }
  .hm-city {
    display: flex; align-items: center; gap: 14px; flex: 0 1 320px; min-height: 76px;
    padding: 12px 16px; text-align: left;
    background: #fff; border: 1px solid var(--blue-100); border-radius: 18px;
    box-shadow: var(--shadow-md); color: inherit; text-decoration: none;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  }
  a.hm-city:hover {
    border-color: var(--blue-400); box-shadow: var(--shadow-lg);
    transform: translateY(-2px); color: inherit;
  }
  .hm-city-pin {
    width: 48px; height: 48px; border-radius: 14px; flex: none;
    display: flex; align-items: center; justify-content: center;
    background: var(--blue-600); color: #fff; font-size: 21px;
  }
  .hm-city-body { flex: 1; min-width: 0; }
  .hm-city-name {
    display: block; font-family: var(--font-display);
    font-size: 1.2rem; font-weight: 800; color: var(--gray-900);
  }
  .hm-city-meta { display: block; margin-top: 2px; font-size: 13px; color: var(--gray-600); }
  .hm-city-go { flex: none; font-size: 18px; color: var(--blue-600); }
  .hm-skel { background: linear-gradient(90deg, var(--gray-100), var(--gray-50), var(--gray-100)); background-size: 200% 100%; animation: hmShimmer 1.4s linear infinite; }
  @keyframes hmShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

  .hm-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; margin-bottom: 20px; }
  .hero-book-btn { padding: 13px 26px; font-size: 15px; font-weight: 600; }
  .hm-trust {
    display: flex; flex-wrap: wrap; justify-content: center; gap: 6px 20px;
    list-style: none; margin: 0; padding: 0;
    font-size: 13.5px; color: var(--gray-600);
  }
  .hm-trust li::before { content: '✓'; color: var(--color-success-text); font-weight: 700; margin-right: 6px; }

  /* ── SECTIONS ── */
  .hm-section { padding: clamp(36px, 5vw, 64px) 0; background: #fff; }
  .hm-alt { background: var(--gray-50); border-top: 1px solid var(--blue-50); border-bottom: 1px solid var(--blue-50); }
  #hospitals { scroll-margin-top: 72px; }
  .hm-head {
    display: flex; align-items: flex-end; justify-content: space-between;
    flex-wrap: wrap; gap: 16px; margin-bottom: 24px;
  }
  .hm-head h2 { margin: 0; }
  .hm-viewall { padding: 10px 20px; font-size: 14px; }

  /* Specialties */
  .hm-spec-grid { display: grid; grid-template-columns: repeat(8, minmax(0, 1fr)); gap: 12px; margin-top: 14px; }
  .hm-spec {
    display: flex; flex-direction: column; align-items: center; gap: 9px;
    padding: 14px 6px; text-align: center; min-height: 44px;
    background: #fff; border: 1px solid var(--blue-100); border-radius: 16px;
    font-size: 13px; font-weight: 600; color: var(--gray-900); text-decoration: none;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  }
  .hm-spec:hover { border-color: var(--blue-400); box-shadow: var(--shadow-md); transform: translateY(-2px); color: var(--gray-900); }
  .hm-spec-icon {
    width: 44px; height: 44px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    background: var(--blue-50); color: var(--blue-600);
  }
  .hm-spec-icon svg { width: 22px; height: 22px; display: block; }

  /* Hospitals */
  .hm-hosp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
  .hm-hosp {
    display: flex; align-items: center; gap: 14px; padding: 16px;
    background: #fff; border: 1px solid var(--blue-100); border-radius: 16px;
    color: inherit; text-decoration: none;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  }
  .hm-hosp:hover { border-color: var(--blue-400); box-shadow: var(--shadow-md); transform: translateY(-2px); color: inherit; }
  .hm-hosp-icon {
    width: 48px; height: 48px; border-radius: 13px; flex: none;
    display: flex; align-items: center; justify-content: center;
    background: var(--blue-50); color: var(--blue-600); font-size: 22px;
  }
  .hm-hosp-body { flex: 1; min-width: 0; }
  .hm-hosp-name {
    display: block; font-family: var(--font-display); font-size: 15px; font-weight: 700;
    color: var(--gray-900); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .hm-hosp-meta { display: block; margin-top: 3px; font-size: 13px; color: var(--gray-500); }
  .hm-hosp-go { flex: none; color: var(--gray-400); }

  /* Doctors */
  .hm-doc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 16px; }
  .hm-doc {
    display: flex; flex-direction: column; overflow: hidden;
    background: #fff; border: 1px solid var(--blue-100); border-radius: 18px;
    color: inherit; text-decoration: none;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  }
  .hm-doc:hover { border-color: var(--blue-400); box-shadow: var(--shadow-md); transform: translateY(-3px); color: inherit; }
  .hm-doc-photo {
    position: relative; height: 120px; background: var(--blue-50);
    display: flex; align-items: center; justify-content: center;
    color: var(--blue-400); font-size: 3rem;
  }
  .hm-doc-photo img { width: 100%; height: 100%; object-fit: cover; }
  .hm-doc-initials {
    width: 58px; height: 58px; border-radius: 50%; background: #fff;
    border: 2px solid var(--blue-100); color: var(--blue-600);
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-display); font-size: 1.2rem; font-weight: 800;
  }
  .hm-doc-live {
    position: absolute; top: 10px; left: 10px; padding: 3px 10px; border-radius: 100px;
    background: var(--color-success-bg); border: 1px solid var(--color-success-border);
    color: var(--color-success-text); font-size: 11px; font-weight: 700;
  }
  .hm-doc-info { padding: 14px 14px 10px; flex: 1; }
  .hm-doc-spec { font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: var(--blue-600); margin-bottom: 4px; }
  .hm-doc-name { font-family: var(--font-display); font-size: 15px; font-weight: 700; color: var(--gray-900); margin-bottom: 6px; }
  .hm-doc-hosp, .hm-doc-meta { font-size: 12.5px; color: var(--gray-500); }
  .hm-doc-hosp { margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .hm-doc-meta { display: flex; justify-content: space-between; gap: 8px; }
  .hm-doc-book {
    display: block; padding: 11px 14px; text-align: center;
    border-top: 1px solid var(--blue-50); background: var(--blue-50);
    font-size: 14px; font-weight: 600; color: var(--blue-700);
  }

  /* How it works */
  .hm-steps {
    display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px;
    list-style: none; margin: 0; padding: 0;
  }
  .hm-steps li {
    display: flex; align-items: center; gap: 12px; padding: 14px 16px;
    background: #fff; border: 1px solid var(--blue-100); border-radius: 14px;
    font-size: 14.5px; font-weight: 600; color: var(--gray-900);
  }
  .hm-step-icon {
    width: 40px; height: 40px; border-radius: 11px; flex: none;
    display: flex; align-items: center; justify-content: center;
    background: var(--blue-50); color: var(--blue-600); font-size: 19px;
  }
  .hm-step-num { display: block; font-size: 11px; font-weight: 700; letter-spacing: 1px; color: var(--blue-400); }

  /* Fees */
  .hm-fee-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; max-width: 780px; }
  .hm-fee {
    position: relative; padding: 22px 24px; border-radius: 18px;
    background: var(--blue-50); border: 1px solid var(--blue-100);
  }
  .hm-fee-pass { background: #fff; border-color: var(--blue-200); }
  .hm-fee-badge {
    position: absolute; top: 18px; right: 18px; padding: 3px 12px; border-radius: 100px;
    background: var(--blue-600); color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase;
  }
  .hm-fee-name { font-size: 14px; font-weight: 600; color: var(--gray-600); margin-bottom: 6px; }
  .hm-fee-amt { font-family: var(--font-display); font-size: 2.6rem; font-weight: 800; line-height: 1; color: var(--blue-700); margin-bottom: 8px; }
  .hm-fee-amt sup { font-size: 1.1rem; font-weight: 600; vertical-align: super; margin-right: 2px; }
  .hm-fee-note { margin: 0; font-size: 13px; line-height: 1.6; color: var(--gray-500); }

  /* For hospitals */
  .hm-biz {
    text-align: center; color: #fff; border-radius: 24px; position: relative; overflow: hidden;
    padding: clamp(32px, 5vw, 52px) clamp(20px, 5vw, 48px);
    background: linear-gradient(135deg, var(--blue-700) 0%, var(--blue-900) 100%);
  }
  .hm-biz h2 { color: #fff; font-size: clamp(1.4rem, 3.2vw, 2.1rem); margin-bottom: 24px; }
  .hm-biz-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; }
  .btn-white, .btn-white-outline {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border-radius: 12px; padding: 13px 24px; font-family: var(--font-body);
    font-size: 15px; font-weight: 600; cursor: pointer; text-decoration: none; transition: all 0.2s;
  }
  .btn-white { background: #fff; color: var(--blue-700); border: none; }
  .btn-white:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.15); color: var(--blue-800); }
  .btn-white-outline { background: transparent; color: rgba(255,255,255,0.92); border: 1px solid rgba(255,255,255,0.4); }
  .btn-white-outline:hover { border-color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.1); color: #fff; }

  @keyframes twPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  /* ── Tablet ── */
  @media (max-width: 991px) {
    .hm-spec-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .hm-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  /* ── Mobile ── */
  @media (max-width: 767px) {
    .hero-sub { margin-bottom: 22px; }
    .hm-city { flex: 1 1 100%; }
    .hero-book-btn { flex: 1 1 100%; justify-content: center; }
    /* A swipeable row keeps eight doctors from becoming a mile of scrolling. */
    .hm-doc-grid {
      display: flex; overflow-x: auto; scroll-snap-type: x mandatory;
      margin: 0 -24px; padding: 0 24px 10px; scroll-padding: 0 24px; scrollbar-width: none;
    }
    .hm-doc-grid::-webkit-scrollbar { display: none; }
    .hm-doc { flex: 0 0 220px; scroll-snap-align: start; }
    .hm-hosp-grid { grid-template-columns: minmax(0, 1fr); }
  }
  @media (max-width: 480px) {
    .hero-badge { font-size: 12px; padding: 5px 13px; }
    .hero-search { padding: 6px 6px 6px 14px; border-radius: 14px; }
    .hero-search-input { padding: 11px 4px; font-size: 14px; }
    .hero-search-btn { padding: 11px 16px; font-size: 13.5px; }
    .hm-steps { grid-template-columns: minmax(0, 1fr); }
    .hm-biz-actions > a { flex: 1 1 100%; }
  }
`;
